import { createContext, useContext } from 'react';
import { C, O } from 'ts-toolbelt';
import * as di from 'tsyringe';
import { FCC } from '~/views/types';

export const Singleton = di.singleton;
export const Inject = di.inject;
export const Injectable = di.injectable;

export const DelayInject = (token: () => Svc) => di.inject(di.delay(token));

export const useService: Injector = (...svcs: Svc[]) => {
    const app = useContext(ctx);
    return svcs.length === 1 ? app.resolve(svcs[0]) : svcs.map(svc => app.resolve(svc));
};

export const AppContainerProvider: FCC = ({ children }) => {
    return <ctx.Provider value={di.container}>{children}</ctx.Provider>;
};

export interface Injector {
    <T extends Svc>(serviceClass: T): Readonly<InstanceType<T>>;
    <T1 extends Svc, T2 extends Svc, A extends [...Svc[]]>(
        svc1: T1,
        svc2: T2,
        ...svcs: [...A]
    ): [Readonly<InstanceType<T1>>, Readonly<InstanceType<T2>>, ...ReadonlyTypes<A>];
}
export abstract class BaseService {
    protected log = (...args: any[]) => {
        console.log(this.constructor.name, ...args);
    };
    protected get self(): O.Writable<this> {
        return this as any;
    }
}

export abstract class BaseSingletonService extends BaseService {
    constructor() {
        super();
        const ctor = this.constructor;
        if (singlestones.has(ctor)) {
            console.warn('Duplicate service created: ' + ctor.name);
        }

        singlestones.add(ctor);
    }
}

const singlestones = new Set<Function>();
const ctx = createContext<Container>(null!);

type Container = di.DependencyContainer;
type Svc = C.Class<[...any], any>;
type ReadonlyTypes<Tuple extends [...Svc[]]> = {
    [Index in keyof Tuple]: Readonly<Tuple[Index] extends Svc ? InstanceType<Tuple[Index]> : 'NOT_A_CLASS'>;
};
